import React, { useEffect } from "react";
import { Image, type ImageSourcePropType } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
  withDelay,
} from "react-native-reanimated";
import { View, ViewStyle } from "react-native";

// Static mapping for level images (require() doesn't support dynamic paths)
export const levelImages: Record<string, ReturnType<typeof require>> = {
  listener: require("@assets/stats/levels/listener.webp"),
  fan: require("@assets/stats/levels/fan.webp"),
  supporter: require("@assets/stats/levels/supporter.webp"),
  advocate: require("@assets/stats/levels/advocate.webp"),
  patron: require("@assets/stats/levels/patron.webp"),
  superfan: require("@assets/stats/levels/superfan.webp"),
  champion: require("@assets/stats/levels/champion.webp"),
};

// Fallback shadow colors matching each level's icon
// These should come from the active gamification tier config.
// Kept as fallback for edge cases where config isn't loaded yet
export const levelShadowColors: Record<string, string> = {
  listener: "#A78BFA", // Purple (headphones)
  fan: "#3B82F6", // Blue (heart)
  supporter: "#2DD4BF", // Teal (speaker)
  advocate: "#4ADE80", // Green (megaphone)
  champion: "#FBBF24", // Gold (medal)
  superfan: "#FB923C", // Orange (star)
  patron: "#EF4444", // Red (crown)
};

// Per-level badge design tokens for score pill gradients
// textLeft/textRight = horizontal gradient on score text
// border = pill outline color, bgTop = pill gradient top color (bottom is always #101010)
export const levelBadgeConfig: Record<
  string,
  { textLeft: string; textRight: string; border: string; bgTop: string }
> = {
  listener: {
    textLeft: "#A17DEF",
    textRight: "#FFFFFF",
    border: "#A17DEF30",
    bgTop: "#1A1525",
  },
  fan: {
    textLeft: "#005CD1",
    textRight: "#80EFFD",
    border: "#5EB5D430",
    bgTop: "#121D25",
  },
  supporter: {
    textLeft: "#7CBFFC",
    textRight: "#FFFFFF",
    border: "#2177B530",
    bgTop: "#121A22",
  },
  advocate: {
    textLeft: "#5BDC04",
    textRight: "#FFFFFF",
    border: "#35A04630",
    bgTop: "#131E12",
  },
  champion: {
    textLeft: "#EAA003",
    textRight: "#FFFFFF",
    border: "#9C870030",
    bgTop: "#1C1A10",
  },
  superfan: {
    textLeft: "#DA5263",
    textRight: "#F29951",
    border: "#F2995120",
    bgTop: "#1E1412",
  },
  patron: {
    textLeft: "#E1464D",
    textRight: "#FFFFFF",
    border: "#E1464D20",
    bgTop: "#1E1214",
  },
};
export const DEFAULT_BADGE_CONFIG = {
  textLeft: "#808080",
  textRight: "#FFFFFF",
  border: "#80808030",
  bgTop: "#181818",
};

// Balatro-style floating animation with 3D tilt and dynamic shadows
export interface FloatingImageProps {
  source: ImageSourcePropType;
  size: number;
  shadowColor?: string;
  /** Phase offset (0-1) to desync animations between icons */
  phaseOffset?: number;
  /** Base duration in ms */
  baseDuration?: number;
  /** Opacity of the image (0-1) */
  opacity?: number;
}

export const FloatingImage = ({
  source,
  size,
  shadowColor = "#000",
  phaseOffset = 0,
  baseDuration = 2500,
  opacity = 1,
}: FloatingImageProps) => {
  // Rotation values for 3D tilt (in degrees)
  const rotateX = useSharedValue(0);
  const rotateY = useSharedValue(0);
  // Subtle vertical float
  const translateY = useSharedValue(0);

  useEffect(() => {
    // Different durations for each axis creates organic movement
    const xRotDuration = baseDuration + phaseOffset * 600;
    const yRotDuration = baseDuration * 1.4 + phaseOffset * 500;
    const floatDuration = baseDuration * 0.8 + phaseOffset * 300;

    // Stagger start times based on phase
    const delay = phaseOffset * 800;

    // Tilt forward/back: ±8 degrees
    rotateX.value = withDelay(
      delay,
      withRepeat(
        withTiming(18, {
          duration: xRotDuration,
          easing: Easing.inOut(Easing.sin),
        }),
        -1,
        true,
      ),
    );

    // Tilt left/right: ±6 degrees (slightly less than X)
    rotateY.value = withDelay(
      delay + 150,
      withRepeat(
        withTiming(-16, {
          duration: yRotDuration,
          easing: Easing.inOut(Easing.sin),
        }),
        -1,
        true,
      ),
    );

    // Gentle float up/down: ±3 pixels
    translateY.value = withDelay(
      delay + 100,
      withRepeat(
        withTiming(-3, {
          duration: floatDuration,
          easing: Easing.inOut(Easing.sin),
        }),
        -1,
        true,
      ),
    );
  }, [baseDuration, phaseOffset, rotateX, rotateY, translateY]);

  // 3D transform with perspective
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 200 },
      { rotateX: `${rotateX.value}deg` },
      { rotateY: `${rotateY.value}deg` },
      { translateY: translateY.value },
    ],
  }));

  // Dynamic shadow that moves opposite to tilt for 3D effect
  // Note: Android doesn't support colored shadows - only iOS gets the glow effect
  const shadowStyle = useAnimatedStyle(() => ({
    // iOS shadow properties (Android ignores these)
    shadowOffset: {
      width: -rotateY.value * 0.2,
      height: rotateX.value * 0.25 + 2,
    },
    shadowOpacity: 0.5 + Math.abs(rotateX.value) * 0.02,
    shadowRadius: 8 + Math.abs(rotateX.value) * 0.3,
  }));

  return (
    <Animated.View
      style={[
        {
          shadowColor,
        },
        shadowStyle,
      ]}
    >
      <Animated.View style={animatedStyle}>
        <Image source={source} style={{ width: size, height: size, opacity }} />
      </Animated.View>
    </Animated.View>
  );
};

// Preset sizes for common use cases
export type LevelBadgeSize = "small" | "medium" | "large";

const SIZE_CONFIG: Record<LevelBadgeSize, { container: number; icon: number }> =
  {
    small: { container: 56, icon: 32 },
    medium: { container: 80, icon: 48 },
    large: { container: 100, icon: 60 },
  };

export interface LevelBadgeProps {
  /** Level name (lowercase) - used to look up the image and shadow color */
  levelName: string;
  /** Preset size or custom number for container size */
  size?: LevelBadgeSize | number;
  /** Custom icon size (optional, calculated from container if not provided) */
  iconSize?: number;
  /** Override shadow color (defaults to level-specific color) */
  shadowColor?: string;
  /** Phase offset for animation (0-1), useful when multiple badges are shown */
  phaseOffset?: number;
  /** Base duration for animation in ms */
  baseDuration?: number;
  /** Opacity of the icon (0-1) */
  opacity?: number;
  /** Show circular background behind the icon */
  showBackground?: boolean;
  /** Background color (solid color string) */
  backgroundColor?: string;
  /** Border width */
  borderWidth?: number;
  /** Border color */
  borderColor?: string;
  /** Additional container style */
  containerStyle?: ViewStyle;
}

/**
 * LevelBadge - A standardized component for displaying level icons
 * with 3D floating animation and optional styled background.
 *
 * Used across the stats screens:
 * - You/index.tsx (StatsTrio): Shows current level with animated icon
 * - YouBreakdownModal: Shows level progression with backgrounds
 */
export const LevelBadge = ({
  levelName,
  size = "medium",
  iconSize: customIconSize,
  shadowColor,
  phaseOffset = 0,
  baseDuration = 2500,
  opacity = 1,
  showBackground = false,
  backgroundColor = "rgba(128, 128, 0, 0.4)",
  borderWidth = 0,
  borderColor = "transparent",
  containerStyle,
}: LevelBadgeProps) => {
  const levelKey = levelName.toLowerCase();
  const imageSource = levelImages[levelKey];
  const resolvedShadowColor =
    shadowColor ?? levelShadowColors[levelKey] ?? "#FFEB3B";

  // Resolve sizes
  const containerSize =
    typeof size === "number" ? size : SIZE_CONFIG[size].container;
  const iconSize =
    customIconSize ??
    (typeof size === "number" ? size * 0.6 : SIZE_CONFIG[size].icon);

  if (!imageSource) {
    // Fallback for unknown levels
    return (
      <View
        style={{
          width: containerSize,
          height: containerSize,
          borderRadius: containerSize / 2,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: showBackground ? backgroundColor : "transparent",
          borderWidth,
          borderColor,
          overflow: "hidden",
          ...containerStyle,
        }}
      />
    );
  }

  const content = (
    <FloatingImage
      source={imageSource}
      size={iconSize}
      shadowColor={resolvedShadowColor}
      phaseOffset={phaseOffset}
      baseDuration={baseDuration}
      opacity={opacity}
    />
  );

  if (!showBackground) {
    return content;
  }

  return (
    <View
      style={{
        width: containerSize,
        height: containerSize,
        borderRadius: containerSize / 2,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor,
        borderWidth,
        borderColor,
        overflow: "hidden",
        ...containerStyle,
      }}
    >
      {content}
    </View>
  );
};

export default LevelBadge;
